import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import StripeEmv from '../components/StripeEmv';
import { toDecimals } from 'checkout/helpers/currency';
import {
  addServerErrors,
  setCheckoutOverlay,
  setConfirmation,
  goToConfirmation,
  clearAlerts,
  addAlert,
  setInteractionBlock,
} from '../../../actions/ui';
import {
  addPayment,
  setConnectedTerminal,
  setIsReaderConnected,
  createStripePaymentIntent,
} from '../../../actions/payment';
import { getCheckoutClient } from '../../../config/CheckoutClient';
import { deleteReservation } from 'checkout/actions/reservation';
import { fetchConnectionToken, discoverAndConnectToReader } from '../actions/stripeEmv';
import { isReservationFullyPaid } from 'checkout/selectors/reservation';
import { getOrderTotal } from 'checkout/selectors/cart';
import { getIsDevMode } from 'checkout/selectors/ui';
import { EVENT_SUBMIT_CARD } from '../../../../helpers/setupKioskConsole';

class StripeEmvContainer extends Component {
  pendingPaymentIntentSecret = null;
  paymentId = null;

  constructor(props) {
    super(props);
    this.state = {
      cancelablePayment: false,
      error: '',
    };
  }

  componentDidMount() {
    if (!this.props.isDevMode) {
      this.props.setInteractionBlock(true);
      this.props.discoverAndConnectToReader(this.props.fetchConnectionToken, this.createPaymentIntent);
    }
    window.addEventListener(EVENT_SUBMIT_CARD, this.submitDummyPayment);
  }

  componentWillUnmount() {
    window.removeEventListener(EVENT_SUBMIT_CARD, this.submitDummyPayment);
    if (this.pendingPaymentIntentSecret) {
      this.cancelPendingPayment();
    }
  }

  render() {
    return <StripeEmv loading={this.props.loading} error={this.state.error} total={this.props.total} />;
  }

  createPaymentIntent = () => {
    let total = toDecimals({ amount: this.props.totalCost, currency: this.props.currency });
    this.props
      .createStripePaymentIntent(total)
      .then((data) => {
        if (data) {
          this.pendingPaymentIntentSecret = data.client_secret;
          this.collectPaymentMethod(data.client_secret);
        }
      })
      .catch((error) => {
        this.setState({ error: 'There was a problem.  Please cancel this order and try again.' });
      });
  };

  collectPaymentMethod = (clientSecret) => {
    this.props.setInteractionBlock(false);
    return this.props.connectedTerminal.collectPaymentMethod(clientSecret).then((result) => {
      if (result.error) {
        if (
          result.error.message !== 'Action canceled.' && // message when we cancel the payment intent
          result.error.message !== 'Rpc Error: Transaction is currently not in progress. ' // message when the cancel button on the reader is pressed
        ) {
          this.cancelPendingPayment();
          // console.log('Error message: "'+result.error.message+'"');
          this.props.addAlert('checkout__error_processing_payment');
          this.props.setIsReaderConnected(false);
        }

        // this.props.deleteReservation();
      } else {
        this.confirmPaymentIntent(result.paymentIntent);
      }
    });
  };

  confirmPaymentIntent = (paymentIntent) => {
    // if this gets duplicated due to network conditions then don't resend it
    if (this.paymentId && paymentIntent.id === this.paymentId) {
      return;
    } else {
      this.paymentId = paymentIntent.id;
    }

    return this.props.connectedTerminal.processPayment(paymentIntent).then((confirmResult) => {
      if (confirmResult.error) {
        this.setState({ error: confirmResult.error.message });
        // recreate payment intent to reactivate the terminal
        this.createPaymentIntent();
      } else if (confirmResult.paymentIntent) {
        this.setState({ error: '' });
        this.submitPayment(confirmResult.paymentIntent);
      }
    });
  };

  // Note: this can only be done before calling `confirmPaymentIntent`.
  cancelPendingPayment = () => {
    this.props.connectedTerminal.cancelCollectPaymentMethod();
  };

  // submitting dummy payments only work if dummy gateway enabled on the server
  submitDummyPayment = (e) => {
    this.props.setInteractionBlock(true);
    const payload = {
      name: 'Dummy Terminal',
      number: e.detail,
      expiration_month: '01',
      expiration_year: new Date().getFullYear() + 1,
      security_code: '123',
      address: '123 Test Street',
      address2: '',
      city: 'City',
      state: 'PA',
      country: 'US',
      zip: '15151',
      phone: this.props.phone,
      amount: this.props.totalCost,
      email: 'dummy@leapevent.tech',
      tos_accepted: true,
      captureMethod: this.props.captureMethod,
      emailOptOut: true,
      leapOptIn: false,
    };
    this.sendPaymentRequest(payload);
  }

  submitPayment = (paymentIntent) => {
    this.props.setInteractionBlock(true);

    if (this.props.isReservationFullyPaid) {
      return;
    }

    this.onPaymentInitiated();

    const chargeData = paymentIntent.charges.data[0];
    const paymentDetails = chargeData.payment_method_details;
    const cardData = paymentDetails.card_present;
    let payload = {
      name: 'Stripe Terminal',
      number: cardData.last4,
      card_type: cardData.brand,
      expiration_month: cardData.exp_month,
      expiration_year: cardData.exp_year,
      security_code: '',
      amount: paymentIntent.amount,
      email: '',
      tos_accepted: this.props.tosAccepted,
      captureMethod: this.props.captureMethod,
      terminalId: this.props.terminalId,
      card_token: paymentIntent.id,
      card_token_type: 2,
    };

    this.sendPaymentRequest(payload);
  };

  sendPaymentRequest = payload => {
    getCheckoutClient(this.props.apiUrl)
      .reservationCreateRequest(this.props.token, 'payment', payload, this.props.secret)
      .then((result) => {
        this.props.setInteractionBlock(false);
        this.onPaymentSuccess(result.body.data);
      })
      .catch((error) => {
        console.error(error);
        this.props.setInteractionBlock(false);
        this.onPaymentFail(error.response?.body?.errors);
      });
  };

  onPaymentInitiated = () => {
    this.props.setInteractionBlock(true);
  };

  onPaymentSuccess = (data) => {
    this.props.setInteractionBlock(false);
    this.props.clearAlerts();
    this.props.addPayment(data);
    this.props.goToConfirmation();
    this.pendingPaymentIntentSecret = null;
  };

  onPaymentFail = (errors) => {
    this.props.setInteractionBlock(false);
    this.props.clearAlerts();
    this.props.addServerErrors(errors);
  };
}

const mapStateToProps = (state) => {
  return {
    apiUrl: state.ui.apiUrl,
    terminalId: state.payment.terminalId,
    totalCost: state.reservation.total_cost ? state.reservation.total_cost.amount : 0,
    currency: state.reservation.total_cost ? state.reservation.total_cost.currency : '',
    sellerId: state.event.seller_id,
    token: state.reservation.token,
    secret: state.reservation.secret,
    terminals: state.payment.terminals,
    stripeTerminal: state.payment.stripeTerminal,
    connectedTerminal: state.payment.connectedTerminal,
    isReaderConnected: state.payment.isReaderConnected,
    loading: state.ui.interactionBlock,
    isReservationFullyPaid: isReservationFullyPaid(state),
    total: getOrderTotal(state),
    isDevMode: getIsDevMode(state),
    gatewayKey: state.payment.gatewayKey,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      addServerErrors,
      addPayment,
      setCheckoutOverlay,
      goToConfirmation,
      setConfirmation,
      clearAlerts,
      deleteReservation,
      fetchConnectionToken,
      setConnectedTerminal,
      addAlert,
      setIsReaderConnected,
      discoverAndConnectToReader,
      setInteractionBlock,
      createStripePaymentIntent,
    },
    dispatch,
  );
};

StripeEmvContainer.defaultProps = {
  captureMethod: 1, // CAPTURE_ONLINE
  tosAccepted: true,
};

StripeEmvContainer.propTypes = {};

export default connect(mapStateToProps, mapDispatchToProps)(StripeEmvContainer);
